// Pestañas "Horas con más clientes" y "Mesas y zonas" del Reporte de ventas.
// Las dos leen GET /caja/reporte-clientes (cajaController.getReporteClientes)
// con el mismo rango de fechas que el resto del reporte.
import { useEffect, useState } from 'react';
import { cajaService } from '../services/api';

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' };
const h2: React.CSSProperties = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', margin: '0 0 16px' };
const th: React.CSSProperties = { color: 'var(--text-muted)', fontWeight: 600, padding: '0 10px 8px', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'right' };
const td: React.CSSProperties = { padding: '9px 10px', borderTop: '1px solid var(--line-2)', textAlign: 'right', color: 'var(--text)' };
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const $ = (n: any) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const hora = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? ' am' : ' pm'}`;

function useReporteClientes(fi: string, ff: string) {
  const [datos, setDatos] = useState<any>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let vivo = true;
    setError('');
    cajaService.getReporteClientes(fi, ff)
      .then(r => { if (vivo) setDatos(r.data); })
      .catch(e => { if (vivo) { setDatos(null); setError(e.response?.data?.error || 'No se pudo consultar'); } });
    return () => { vivo = false; };
  }, [fi, ff]);
  return { datos, error };
}

function Kpis({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 12 }}>
      {items.map(k => (
        <div key={k.label} style={{ ...card, padding: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', margin: 0 }}>{k.label}</p>
          <p style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', margin: '6px 0 0', color: 'var(--text)' }}>{k.value}</p>
        </div>
      ))}
    </div>
  );
}

// Aviso honesto: sin personas capturadas, la cuenta vale por 1 cliente.
function NotaPersonas({ r }: { r: any }) {
  if (!r?.sin_personas) return null;
  return (
    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
      {r.sin_personas} de {r.cuentas} cuentas no tienen capturado el número de personas; cada una se cuenta como 1 cliente.
      Para que el reporte sea exacto, que el mesero ponga las personas al abrir la mesa.
    </p>
  );
}

/* ═══ Horas con más clientes ═════════════════════════════════════════════ */
export function ReporteHoras({ fechaInicio, fechaFin }: { fechaInicio: string; fechaFin: string }) {
  const { datos, error } = useReporteClientes(fechaInicio, fechaFin);
  if (error) return <div style={{ ...card, padding: 14, color: 'var(--brand-dark)' }}>{error}</div>;
  if (!datos) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</p>;

  const r = datos.resumen;
  const horas: any[] = datos.por_hora;
  if (!horas.length) return <div style={{ ...card, padding: 20, color: 'var(--text-faint)', fontSize: 13 }}>Sin cuentas en este período</div>;

  // Eje continuo de la primera a la última hora con movimiento
  const desde = horas[0].hora, hasta = horas[horas.length - 1].hora;
  const porHora = Object.fromEntries(horas.map(h => [h.hora, h]));
  const rango = Array.from({ length: hasta - desde + 1 }, (_, i) => desde + i);
  const max = Math.max(1, ...horas.map(h => h.clientes));
  const pico = horas.reduce((a, b) => (b.clientes > a.clientes ? b : a));

  const calor: Record<string, number> = Object.fromEntries(datos.dia_hora.map((d: any) => [`${d.dow}-${d.hora}`, d.clientes]));
  const maxCalor = Math.max(1, ...datos.dia_hora.map((d: any) => d.clientes));
  const ordenDias = [1, 2, 3, 4, 5, 6, 0];   // lunes primero

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Kpis items={[
        { label: 'Hora pico', value: `${hora(pico.hora)} – ${hora((pico.hora + 1) % 24)}` },
        { label: 'Clientes en la hora pico', value: String(pico.clientes) },
        { label: 'Clientes en el período', value: String(r.clientes) },
        { label: 'Clientes por día', value: (r.clientes / Math.max(1, r.dias_operados)).toFixed(1) },
      ]} />

      <div style={{ ...card, padding: 20, overflowX: 'auto' }}>
        <h2 style={h2}>Clientes por hora de llegada</h2>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200 }}>
          {rango.map(h => {
            const d = porHora[h];
            const n = d?.clientes || 0;
            return (
              <div key={h} title={d ? `${hora(h)}: ${n} clientes · ${d.cuentas} cuentas · ${$(d.total)}` : `${hora(h)}: sin clientes`}
                style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 4, cursor: 'default' }}>
                {h === pico.hora && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)' }}>{n}</span>}
                <div style={{ width: '100%', maxWidth: 38, height: `${(n / max) * 100}%`, minHeight: n ? 2 : 0, background: 'var(--brand-texto)', opacity: h === pico.hora ? 1 : 0.55, borderRadius: '4px 4px 0 0' }} />
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 6, borderTop: '1px solid var(--line)', paddingTop: 6 }}>
          {rango.map(h => <span key={h} style={{ flex: 1, textAlign: 'center', fontSize: 10.5, color: 'var(--text-muted)' }}>{hora(h)}</span>)}
        </div>
      </div>

      <div style={{ ...card, padding: 20, overflowX: 'auto' }}>
        <h2 style={h2}>Día de la semana × hora</h2>
        <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: 11 }}>
          <thead>
            <tr><th />{rango.map(h => <th key={h} style={{ fontWeight: 500, color: 'var(--text-muted)', padding: '0 2px 4px' }}>{hora(h)}</th>)}</tr>
          </thead>
          <tbody>
            {ordenDias.map(dow => (
              <tr key={dow}>
                <td style={{ paddingRight: 8, color: 'var(--text-muted)', fontWeight: 600 }}>{DIAS[dow]}</td>
                {rango.map(h => {
                  const n = calor[`${dow}-${h}`] || 0;
                  return (
                    <td key={h} title={`${DIAS[dow]} ${hora(h)}: ${n} clientes`}
                      style={{ width: 38, height: 26, borderRadius: 4, textAlign: 'center',
                        background: n ? `color-mix(in srgb, var(--brand-texto) ${Math.round(15 + (n / maxCalor) * 85)}%, var(--surface))` : 'var(--bg)',
                        color: n / maxCalor > 0.55 ? 'var(--bg)' : 'var(--text)' }}>
                      {n || ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 0' }}>Más oscuro = más clientes. Suma de todo el período.</p>
      </div>

      <div style={{ ...card, padding: 20, overflowX: 'auto' }}>
        <h2 style={h2}>Detalle por hora</h2>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr><th style={{ ...th, textAlign: 'left' }}>Hora</th><th style={th}>Clientes</th><th style={th}>Cuentas</th><th style={th}>Promedio por día</th><th style={th}>Venta</th></tr></thead>
          <tbody>
            {horas.map(h => (
              <tr key={h.hora}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{hora(h.hora)} – {hora((h.hora + 1) % 24)}</td>
                <td style={td}>{h.clientes}</td>
                <td style={td}>{h.cuentas}</td>
                <td style={td}>{(h.clientes / Math.max(1, h.dias)).toFixed(1)}</td>
                <td style={td}>{$(h.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NotaPersonas r={r} />
    </div>
  );
}

/* ═══ Mesas y zonas ══════════════════════════════════════════════════════ */
export function ReporteMesas({ fechaInicio, fechaFin }: { fechaInicio: string; fechaFin: string }) {
  const { datos, error } = useReporteClientes(fechaInicio, fechaFin);
  const [orden, setOrden] = useState<'total' | 'clientes' | 'por_persona' | 'por_cuenta'>('total');
  if (error) return <div style={{ ...card, padding: 14, color: 'var(--brand-dark)' }}>{error}</div>;
  if (!datos) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</p>;
  if (!datos.por_mesa.length) return <div style={{ ...card, padding: 20, color: 'var(--text-faint)', fontSize: 13 }}>Sin cuentas en este período</div>;

  const zonas: any[] = datos.por_zona;
  const mesas: any[] = [...datos.por_mesa].sort((a, b) => b[orden] - a[orden]);
  const maxMesa = Math.max(1, ...mesas.map(m => m[orden]));
  const mejor = [...datos.por_mesa].filter((m: any) => m.mesa != null).sort((a: any, b: any) => b.por_persona - a.por_persona)[0];
  const mejorZona = zonas.filter(z => z.zona !== 'Para llevar').sort((a, b) => b.por_persona - a.por_persona)[0];

  const COLS: { k: typeof orden; label: string }[] = [
    { k: 'total', label: 'Venta' }, { k: 'clientes', label: 'Clientes' },
    { k: 'por_cuenta', label: 'Por cuenta' }, { k: 'por_persona', label: 'Por persona' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Kpis items={[
        { label: 'Mesa que más deja por persona', value: mejor ? `Mesa ${mejor.mesa} · ${$(mejor.por_persona)}` : '—' },
        { label: 'Zona que más deja por persona', value: mejorZona ? `${mejorZona.zona} · ${$(mejorZona.por_persona)}` : '—' },
        { label: 'Cuenta promedio', value: $(datos.resumen.total / Math.max(1, datos.resumen.cuentas)) },
        { label: 'Gasto por persona', value: $(datos.resumen.total / Math.max(1, datos.resumen.clientes)) },
      ]} />

      <div style={{ ...card, padding: 20, overflowX: 'auto' }}>
        <h2 style={h2}>Por zona</h2>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>Zona</th><th style={th}>Mesas</th><th style={th}>Cuentas</th><th style={th}>Clientes</th>
            <th style={th}>Venta</th><th style={th}>Por cuenta</th><th style={th}>Por persona</th><th style={th}>Propinas</th>
          </tr></thead>
          <tbody>
            {zonas.map(z => (
              <tr key={z.zona}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{z.zona}</td>
                <td style={td}>{z.mesas || '—'}</td><td style={td}>{z.cuentas}</td><td style={td}>{z.clientes}</td>
                <td style={{ ...td, fontWeight: 700 }}>{$(z.total)}</td><td style={td}>{$(z.por_cuenta)}</td>
                <td style={td}>{$(z.por_persona)}</td><td style={td}>{$(z.propina)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...card, padding: 20, overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <h2 style={{ ...h2, margin: 0 }}>Por mesa</h2>
          <div style={{ display: 'flex', gap: 4, background: 'var(--well)', borderRadius: 8, padding: 3 }}>
            {COLS.map(c => (
              <button key={c.k} onClick={() => setOrden(c.k)}
                style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: orden === c.k ? 'var(--surface)' : 'transparent', color: orden === c.k ? 'var(--brand-texto)' : 'var(--muted)' }}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, textAlign: 'left' }}>Mesa</th><th style={{ ...th, textAlign: 'left', width: '30%' }} />
            <th style={th}>Cuentas</th><th style={th}>Clientes</th><th style={th}>Venta</th>
            <th style={th}>Por cuenta</th><th style={th}>Por persona</th><th style={th}>Tiempo prom.</th>
          </tr></thead>
          <tbody>
            {mesas.map(m => (
              <tr key={`${m.zona}-${m.mesa}`}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {m.mesa != null ? `Mesa ${m.mesa}` : 'Para llevar'}
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6 }}>{m.mesa != null ? m.zona : ''}</span>
                </td>
                <td style={{ ...td, textAlign: 'left' }}>
                  <div style={{ height: 8, width: `${(m[orden] / maxMesa) * 100}%`, minWidth: 2, background: 'var(--brand-texto)', opacity: 0.7, borderRadius: '0 4px 4px 0' }} />
                </td>
                <td style={td}>{m.cuentas}</td><td style={td}>{m.clientes}</td>
                <td style={{ ...td, fontWeight: 700 }}>{$(m.total)}</td>
                <td style={td}>{$(m.por_cuenta)}</td><td style={td}>{$(m.por_persona)}</td>
                <td style={td}>{m.minutos ? `${Math.round(m.minutos)} min` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 0' }}>
          "Sin zona" incluye mesas que no tenían zona asignada o que ya se dieron de baja. La zona se asigna en Configuración › Mesas.
        </p>
      </div>

      <NotaPersonas r={datos.resumen} />
    </div>
  );
}
